import { getToken, onMessage } from "firebase/messaging";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db, getMessagingInstance } from "../firebase";

const VAPID_KEY =
  "BOSi0E-WalUbqOuB5su9XW2J39ot0D4x-fSPRYo_AxMVloqe7jIkpYXf5J007lXs1D_ZZB9YH1Hc0X-7jQrqR38";

let foregroundUnsubscribe = null;

/**
 * Registers (or refreshes) the current browser's FCM token for the given user
 * and stores it deduplicated in `<collection>/<uid>.fcmTokens`.
 *
 * - Always dedupes by token string (not by object identity), so old entries
 *   with different shapes for the same token are removed.
 * - Sets up a single foreground onMessage listener (replacing any prior one).
 *
 * @param {object} user  Firebase auth user (must have .uid)
 * @param {"lawyers"|"client"} userType
 */
export const registerFcmToken = async (user, userType) => {
  if (!user || !user.uid || !userType) {
    console.log("registerFcmToken: missing user or userType");
    return null;
  }

  const collectionName = userType === "client" ? "client" : "lawyers";
  const userRef = doc(db, collectionName, user.uid);

  try {
    const messaging = await getMessagingInstance();
    if (!messaging) {
      console.log("Firebase Messaging is not supported in this browser");
      return null;
    }

    let permission = Notification.permission;
    if (permission !== "granted") {
      permission = await Notification.requestPermission();
    }
    if (permission !== "granted") {
      console.log("Notification permission denied");
      return null;
    }

    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) {
      console.log("No FCM token available.");
      return null;
    }

    const newTokenObj = {
      token,
      platform: "react",
      updatedAt: new Date().toISOString(),
    };

    const docSnap = await getDoc(userRef);
    const existing = docSnap.exists() ? docSnap.data().fcmTokens || [] : [];

    // Normalize + dedupe by token string. Drop any prior entry that points
    // to the same token (regardless of shape) so we don't accumulate stale
    // copies for the same browser.
    const seen = new Set([token]);
    const cleaned = [];
    for (const entry of existing) {
      const t = typeof entry === "string" ? entry : entry?.token;
      if (!t || typeof t !== "string") continue;
      if (seen.has(t)) continue;
      seen.add(t);
      cleaned.push(typeof entry === "string" ? { token: t, platform: "unknown" } : entry);
    }
    const nextTokens = [newTokenObj, ...cleaned];

    if (docSnap.exists()) {
      await updateDoc(userRef, {
        fcmTokens: nextTokens,
        fcmTokenUpdatedAt: serverTimestamp(),
      });
    } else {
      await setDoc(
        userRef,
        {
          fcmTokens: nextTokens,
          fcmTokenUpdatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    setupForegroundMessaging(messaging);
    return token;
  } catch (err) {
    console.error("registerFcmToken error:", err);
    return null;
  }
};

/**
 * Registers a single foreground message listener. Calling this multiple times
 * is safe — the previous listener is unsubscribed first to prevent duplicate
 * notifications when the user re-logs-in or navigates between auth screens.
 */
export const setupForegroundMessaging = (messagingInstance) => {
  if (foregroundUnsubscribe) {
    try {
      foregroundUnsubscribe();
    } catch (e) {
      // ignore
    }
    foregroundUnsubscribe = null;
  }

  const messaging = messagingInstance;
  if (!messaging) return;

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    console.log("Foreground FCM message:", payload);
    const title =
      payload.notification?.title || payload.data?.title || "New Message";
    const options = {
      body: payload.notification?.body || payload.data?.body || "",
      icon: window.location.origin + "/logo192.png",
      badge: window.location.origin + "/logo192.png",
      data: payload.data || {},
      tag: payload.data?.eventId || payload.messageId, // collapse duplicates
    };
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, options);
      });
    }
  });
};
